import {
  Car,
  Plus,
  Scale,
  Shield,
  Sparkles,
  Stethoscope,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { isIntakeBooleanPath } from '@/components/intakes/utils/formDataSanitizers';
import { cn } from '@/lib/utils';
import {
  useCreateCompanionFromRowMutation,
  useSaveCompanionsMutation,
  useSyncCompanionIntakeMutation,
} from '@/services/api/intakeService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReviewScreenProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFieldChange: (path: string, value: any) => void;
  /** Case description / notes */
  caseDescription?: string;
  onCaseDescriptionChange?: (value: string) => void;
  /** Intake ID for companion case operations */
  intakeId?: string;
  /** AI-generated summary (from server) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  summary?: Record<string, any> | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getVal(obj: Record<string, any>, path: string): any {
  return path.split('.').reduce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (curr: any, key: string) => curr?.[key],
    obj,
  );
}

// ---------------------------------------------------------------------------
// Tab config
// ---------------------------------------------------------------------------

interface TabConfig {
  key: string;
  label: string;
  icon: React.ReactNode;
}

const TABS: TabConfig[] = [
  { key: 'personal', label: 'Personal', icon: <User className="size-3.5" /> },
  { key: 'incident', label: 'Incident', icon: <Car className="size-3.5" /> },
  { key: 'insurance', label: 'Insurance', icon: <Shield className="size-3.5" /> },
  { key: 'medical', label: 'Medical', icon: <Stethoscope className="size-3.5" /> },
  { key: 'legal', label: 'Legal', icon: <Scale className="size-3.5" /> },
  { key: 'companions', label: 'Family', icon: <Users className="size-3.5" /> },
  { key: 'summary', label: 'AI Summary', icon: <Sparkles className="size-3.5" /> },
];

// ---------------------------------------------------------------------------
// Shared sub-components — Cash App style
// ---------------------------------------------------------------------------

const inputClasses =
  'w-full px-3 py-2.5 text-xs font-semibold text-zinc-900 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 focus:border-transparent transition-colors placeholder:text-zinc-400 placeholder:font-normal';

const labelClasses = 'text-[10px] font-bold text-zinc-500 uppercase tracking-wider';

interface FieldProps {
  label: string;
  path: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: Record<string, any>;
  onLocalChange: (path: string, value: string | boolean) => void;
  type?: string;
  multiline?: boolean;
  span2?: boolean;
}

/**
 * Field with focus-gated local buffer.
 * When NOT focused: shows propValue from context (always correct after save/refresh).
 * When focused: shows localValue for smooth typing (no parent re-renders per keystroke).
 * On blur: propagates final value to context, then reverts to showing propValue.
 */
const Field: React.FC<FieldProps> = ({ label, path, formData, onLocalChange, type, multiline, span2 }) => {
  const propValue = String(getVal(formData, path) ?? '');
  const id = `review-${path.replace(/\./g, '-')}`;
  const isTextType = !type || type === 'text';

  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(propValue);
  const localRef = useRef(localValue);
  localRef.current = localValue;

  const handleFocus = useCallback(() => {
    setLocalValue(propValue); // sync before editing starts
    setIsFocused(true);
  }, [propValue]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (localRef.current !== propValue) {
      onLocalChange(path, localRef.current);
    }
  }, [onLocalChange, path, propValue]);

  // Display value: propValue when idle, localValue when editing
  let displayValue = propValue;
  if (isTextType && isFocused) {
    displayValue = localValue;
  }

  return (
    <div className={cn('flex flex-col gap-1.5', span2 && 'col-span-2')}>
      <label htmlFor={id} className={labelClasses}>{label}</label>
      {multiline ? (
        <textarea
          id={id}
          value={displayValue}
          onChange={(e) => isTextType ? setLocalValue(e.target.value) : onLocalChange(path, e.target.value)}
          onFocus={isTextType ? handleFocus : undefined}
          onBlur={isTextType ? handleBlur : undefined}
          rows={3}
          className={cn(inputClasses, 'resize-none min-h-[60px]')}
        />
      ) : (
        <input
          id={id}
          type={type ?? 'text'}
          value={displayValue}
          onChange={(e) => isTextType ? setLocalValue(e.target.value) : onLocalChange(path, e.target.value)}
          onFocus={isTextType ? handleFocus : undefined}
          onBlur={isTextType ? handleBlur : undefined}
          className={inputClasses}
        />
      )}
    </div>
  );
};

interface BoolToggleProps {
  label: string;
  path: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: Record<string, any>;
  onLocalChange: (path: string, value: string | boolean) => void;
}

const BoolToggle: React.FC<BoolToggleProps> = ({ label, path, formData, onLocalChange }) => {
  const raw = getVal(formData, path);
  const isYes = raw === true || raw === 'yes' || raw === 'true';
  const isNo = raw === false || raw === 'no' || raw === 'false';
  const storesBoolean = isIntakeBooleanPath(path);

  // Send boolean for backend boolean fields, string for everything else
  const handleChange = useCallback((val: 'yes' | 'no') => {
    if (storesBoolean) {
      onLocalChange(path, val === 'yes');
    } else {
      onLocalChange(path, val);
    }
  }, [path, onLocalChange, storesBoolean]);

  return (
    <div className="flex flex-col gap-1.5">
      <span className={labelClasses}>{label}</span>
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => handleChange('yes')}
          className={cn(
            'flex-1 h-8 rounded-lg text-[10px] font-black uppercase tracking-wide transition-all duration-100',
            isYes ? 'bg-zinc-900 text-white shadow-sm' : 'bg-white border border-zinc-200 text-zinc-500 hover:border-zinc-300 hover:text-zinc-700',
          )}
        >
          Yes
        </button>
        <button
          type="button"
          onClick={() => handleChange('no')}
          className={cn(
            'flex-1 h-8 rounded-lg text-[10px] font-black uppercase tracking-wide transition-all duration-100',
            isNo ? 'bg-zinc-700 text-white shadow-sm' : 'bg-white border border-zinc-200 text-zinc-500 hover:border-zinc-300 hover:text-zinc-700',
          )}
        >
          No
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Array item card (defendants, treatments, etc.)
// ---------------------------------------------------------------------------

interface ArrayCardProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: Record<string, any>;
  onLocalChange: (path: string, value: string | boolean) => void;
  basePath: string;
  title: string;
  fields: { key: string; label: string; type?: string }[];
}

const ArrayCards: React.FC<ArrayCardProps> = ({ formData, onLocalChange, basePath, title, fields }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items: Record<string, any>[] = getVal(formData, basePath) ?? [];

  return (
    <div className="space-y-2">
      <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">{title}</span>
      {items.length === 0 && (
        <p className="text-xs font-medium text-zinc-400">None recorded</p>
      )}
      {items.map((_, idx) => (
        <div key={idx} className="rounded-xl bg-white border border-zinc-100 p-3 space-y-3">
          <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest">
            {title.replace(/s$/, '')} {idx + 1}
          </span>
          <div className="grid grid-cols-2 gap-3">
            {fields.map(({ key, label, type }) => (
              type === 'boolean' ? (
                <BoolToggle
                  key={key}
                  label={label}
                  path={`${basePath}.${idx}.${key}`}
                  formData={formData}
                  onLocalChange={onLocalChange}
                />
              ) : (
                <Field
                  key={key}
                  label={label}
                  path={`${basePath}.${idx}.${key}`}
                  formData={formData}
                  onLocalChange={onLocalChange}
                  type={type}
                />
              )
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ReviewScreen: React.FC<ReviewScreenProps> = ({
  formData,
  onFieldChange,
  caseDescription,
  onCaseDescriptionChange,
  intakeId,
  summary,
}) => {
  // Companion case mutations
  const [createCompanion, { isLoading: isCreatingCompanion }] = useCreateCompanionFromRowMutation();
  const [syncCompanion, { isLoading: isSyncingCompanion }] = useSyncCompanionIntakeMutation();
  const [saveCompanions] = useSaveCompanionsMutation();

  const handleLocalChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (path: string, value: any) => {
      onFieldChange(path, value);
    },
    [onFieldChange],
  );

  const [activeTab, setActiveTab] = useState('personal');

  const handleDescriptionChange = useCallback(
    (value: string) => {
      onCaseDescriptionChange?.(value);
    },
    [onCaseDescriptionChange],
  );

  // ---------------------------------------------------------------------------
  // Companion case handlers
  // ---------------------------------------------------------------------------

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const companions: Record<string, any>[] = useMemo(() => formData.companions ?? [], [formData.companions]);

  const handleAddCompanion = useCallback(() => {
    const updated = [...companions, { client_name: '', details: null, created_intake_id: null }];
    onFieldChange('companions', updated);
  }, [companions, onFieldChange]);

  const handleRemoveCompanion = useCallback((index: number) => {
    const row = companions[index];
    if (row?.created_intake_id) {
      toast.error('Cannot remove row after intake is created');
      return;
    }
    const updated = companions.filter((_: unknown, i: number) => i !== index);
    onFieldChange('companions', updated);
  }, [companions, onFieldChange]);

  const handleCompanionFieldChange = useCallback((index: number, field: string, value: string) => {
    const updated = companions.map((row: Record<string, unknown>, i: number) =>
      i === index ? { ...row, [field]: value } : row,
    );
    onFieldChange('companions', updated);
  }, [companions, onFieldChange]);

  const handleCreateCompanionIntake = useCallback(async (index: number) => {
    if (!intakeId) return;
    const row = companions[index];
    if (!row?.client_name?.trim()) {
      toast.error('Client name is required');
      return;
    }

    const backendCompanions = companions.map((r: Record<string, string | null>) => ({
      client_name: r.client_name ?? '',
      details: r.details ?? null,
      created_intake_id: r.created_intake_id ?? null,
    }));
    await saveCompanions({ id: intakeId, companions: backendCompanions }).unwrap();

    const result = await createCompanion({ intakeId, rowIndex: index });
    if ('data' in result && result.data) {
      toast.success(`Created intake for ${row.client_name}`);
      const updated = companions.map((r: Record<string, unknown>, i: number) =>
        i === index ? { ...r, created_intake_id: result.data.id } : r,
      );
      onFieldChange('companions', updated);
      window.open(`/intakes/${result.data.id}`, '_blank');
    } else {
      toast.error('Failed to create companion intake');
    }
  }, [intakeId, companions, createCompanion, saveCompanions, onFieldChange]);

  const handleSyncCompanion = useCallback(async (targetIntakeId: string, clientName: string) => {
    if (!intakeId) return;
    const result = await syncCompanion({ intakeId, targetIntakeId });
    if ('data' in result) {
      toast.success(`Synced data to ${clientName}`);
    } else {
      toast.error('Failed to sync data');
    }
  }, [intakeId, syncCompanion]);

  // Tab content
  const tabContent: Record<string, React.ReactNode> = {
    personal: (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Full Name" path="personal_info.full_name" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Date of Birth" path="personal_info.dob" formData={formData} onLocalChange={handleLocalChange} type="date" />
          <Field label="SSN (Last 4)" path="personal_info.ssn_last_4" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Phone" path="personal_info.phone" formData={formData} onLocalChange={handleLocalChange} type="tel" />
          <Field label="Email" path="personal_info.email" formData={formData} onLocalChange={handleLocalChange} type="email" />
          <Field label="Address" path="personal_info.address" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Preferred Contact" path="personal_info.preferred_contact" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Language" path="personal_info.preferred_language" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Driver License #" path="personal_info.drivers_license_number" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="DL State" path="personal_info.drivers_license_state" formData={formData} onLocalChange={handleLocalChange} />
        </div>
        <div className="pt-3 border-t border-zinc-100">
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2 block">Emergency Contact</span>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Name" path="personal_info.emergency_contact.name" formData={formData} onLocalChange={handleLocalChange} />
            <Field label="Phone" path="personal_info.emergency_contact.phone" formData={formData} onLocalChange={handleLocalChange} type="tel" />
            <Field label="Relationship" path="personal_info.emergency_contact.relationship" formData={formData} onLocalChange={handleLocalChange} />
          </div>
        </div>
      </div>
    ),

    incident: (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Date" path="incident_info.date" formData={formData} onLocalChange={handleLocalChange} type="date" />
          <Field label="Time" path="incident_info.time" formData={formData} onLocalChange={handleLocalChange} type="time" />
          <Field label="State" path="incident_info.state" formData={formData} onLocalChange={handleLocalChange} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Location" path="incident_info.location" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Type" path="incident_info.type" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Weather" path="incident_info.weather" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Hospital" path="incident_info.hospital" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Vehicle Type" path="incident_info.vehicle_type" formData={formData} onLocalChange={handleLocalChange} />
        </div>
        <Field label="Description" path="incident_info.description" formData={formData} onLocalChange={handleLocalChange} multiline />
        <Field label="Present Complaints" path="incident_info.present_complaints" formData={formData} onLocalChange={handleLocalChange} multiline />
        <div className="grid grid-cols-2 gap-3">
          <BoolToggle label="EMS Called" path="incident_info.ems" formData={formData} onLocalChange={handleLocalChange} />
          <BoolToggle label="Police Report" path="incident_info.police_report" formData={formData} onLocalChange={handleLocalChange} />
          <BoolToggle label="Photos" path="incident_info.photos" formData={formData} onLocalChange={handleLocalChange} />
          <BoolToggle label="Witnesses" path="incident_info.witnesses" formData={formData} onLocalChange={handleLocalChange} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Police Dept" path="incident_info.police_dept" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Report #" path="incident_info.police_report_number" formData={formData} onLocalChange={handleLocalChange} />
          <BoolToggle label="Other Driver Ticketed" path="incident_info.other_driver_ticket" formData={formData} onLocalChange={handleLocalChange} />
          <BoolToggle label="Admitted Fault" path="incident_info.other_driver_fault" formData={formData} onLocalChange={handleLocalChange} />
        </div>
        <Field label="Witness Contact Info" path="incident_info.witness_contact_info" formData={formData} onLocalChange={handleLocalChange} multiline />
        <div className="grid grid-cols-2 gap-3">
          <BoolToggle label="Owner Knew Hazard" path="incident_info.owner_knew_about_hazard" formData={formData} onLocalChange={handleLocalChange} />
          <BoolToggle label="Security Cameras" path="incident_info.security_cameras" formData={formData} onLocalChange={handleLocalChange} />
          <BoolToggle label="Similar Incidents" path="incident_info.similar_incidents" formData={formData} onLocalChange={handleLocalChange} />
          <BoolToggle label="Filed Complaint" path="incident_info.filed_complaint" formData={formData} onLocalChange={handleLocalChange} />
        </div>
        <Field label="Owner Knew Details" path="incident_info.owner_knew_details" formData={formData} onLocalChange={handleLocalChange} multiline />
      </div>
    ),

    insurance: (
      <div className="space-y-4">
        <ArrayCards formData={formData} onLocalChange={handleLocalChange} basePath="insurance.defendants" title="Defendants" fields={[
          { key: 'name', label: 'Name' },
          { key: 'address', label: 'Address' },
          { key: 'insurance_company_name', label: 'Insurance Company' },
          { key: 'policy_number', label: 'Policy #' },
          { key: 'claim_number', label: 'Claim #' },
        ]} />
        <div className="pt-3 border-t border-zinc-100 space-y-3">
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">PIP / Auto Insurance</span>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Company" path="insurance.pip_insurance.company_name" formData={formData} onLocalChange={handleLocalChange} />
            <Field label="Policy #" path="insurance.pip_insurance.policy_number" formData={formData} onLocalChange={handleLocalChange} />
            <Field label="Claim #" path="insurance.pip_insurance.claim_number" formData={formData} onLocalChange={handleLocalChange} />
          </div>
        </div>
        <div className="pt-3 border-t border-zinc-100 space-y-3">
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Health Insurance</span>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Provider" path="insurance.health_provider" formData={formData} onLocalChange={handleLocalChange} />
            <Field label="Member ID" path="insurance.health_member_id" formData={formData} onLocalChange={handleLocalChange} />
            <Field label="Group ID" path="insurance.health_group_id" formData={formData} onLocalChange={handleLocalChange} />
          </div>
        </div>
      </div>
    ),

    medical: (
      <div className="space-y-4">
        <ArrayCards formData={formData} onLocalChange={handleLocalChange} basePath="medical_wage_history.treatments" title="Treatments" fields={[
          { key: 'provider', label: 'Provider' },
          { key: 'date_of_service', label: 'Date of Service', type: 'date' },
          { key: 'treatment_received', label: 'Treatment' },
          { key: 'still_treating', label: 'Still Treating', type: 'boolean' },
        ]} />
        <div className="pt-3 border-t border-zinc-100 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <BoolToggle label="Lost Wages" path="medical_wage_history.has_lost_wages" formData={formData} onLocalChange={handleLocalChange} />
            <Field label="Employer" path="medical_wage_history.employer" formData={formData} onLocalChange={handleLocalChange} />
            <Field label="Job Title" path="medical_wage_history.job_title" formData={formData} onLocalChange={handleLocalChange} />
            <Field label="Hourly Wage" path="medical_wage_history.hourly_wage" formData={formData} onLocalChange={handleLocalChange} />
          </div>
          <BoolToggle label="Prior Injuries" path="medical_wage_history.prior_injuries" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Prior Injuries Description" path="medical_wage_history.prior_injuries_description" formData={formData} onLocalChange={handleLocalChange} multiline />
          <Field label="Prior Medical History" path="medical_wage_history.prior_medical_history" formData={formData} onLocalChange={handleLocalChange} multiline />
        </div>
      </div>
    ),

    legal: (
      <div className="space-y-3">
        <Field label="Prior Legal History" path="legal_lifestyle.prior_legal_history" formData={formData} onLocalChange={handleLocalChange} multiline />
        <Field label="Lifestyle Impact" path="legal_lifestyle.lifestyle_impact" formData={formData} onLocalChange={handleLocalChange} multiline />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pending Bills" path="legal_lifestyle.pending_bills" formData={formData} onLocalChange={handleLocalChange} />
          <Field label="Out of Pocket Expenses" path="legal_lifestyle.out_of_pocket_expenses" formData={formData} onLocalChange={handleLocalChange} />
        </div>
        <Field label="Other Notes" path="legal_lifestyle.other_notes" formData={formData} onLocalChange={handleLocalChange} multiline />
      </div>
    ),

    companions: (
      <div className="space-y-3">
        {companions.length === 0 && (
          <p className="text-xs font-medium text-zinc-400">No family members added yet.</p>
        )}
        {companions.map((row: Record<string, string | null>, idx: number) => (
          <div key={idx} className="rounded-xl bg-white border border-zinc-100 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest">
                Family Member {idx + 1}
              </span>
              <div className="flex items-center gap-1.5">
                {row.created_intake_id ? (
                  <>
                    <a
                      href={`/intakes/${row.created_intake_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] font-bold text-zinc-600 hover:text-zinc-900 underline"
                    >
                      View Intake
                    </a>
                    <button
                      type="button"
                      onClick={() => handleSyncCompanion(row.created_intake_id as string, row.client_name || 'companion')}
                      disabled={isSyncingCompanion}
                      className="text-[10px] font-bold text-zinc-500 hover:text-zinc-900 px-2 py-1 rounded-lg hover:bg-zinc-100 transition-colors"
                    >
                      {isSyncingCompanion ? 'Syncing...' : 'Sync'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => handleCreateCompanionIntake(idx)}
                      disabled={isCreatingCompanion}
                      className="text-[10px] font-bold text-zinc-600 hover:text-zinc-900 px-2 py-1 rounded-lg hover:bg-zinc-100 transition-colors"
                    >
                      {isCreatingCompanion ? 'Creating...' : 'Create Intake'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveCompanion(idx)}
                      className="text-zinc-400 hover:text-red-500 transition-colors p-1"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="flex flex-col gap-1.5">
                <span className={labelClasses}>Client Name</span>
                <input
                  type="text"
                  value={row.client_name ?? ''}
                  onChange={(e) => handleCompanionFieldChange(idx, 'client_name', e.target.value)}
                  className={inputClasses}
                  disabled={!!row.created_intake_id}
                />
              </label>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="flex flex-col gap-1.5">
                <span className={labelClasses}>Details</span>
                <input
                  type="text"
                  value={row.details ?? ''}
                  onChange={(e) => handleCompanionFieldChange(idx, 'details', e.target.value)}
                  className={inputClasses}
                />
              </label>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={handleAddCompanion}
          className="flex items-center gap-1.5 text-xs font-bold text-zinc-500 hover:text-zinc-900 px-3 py-2 rounded-xl hover:bg-white hover:shadow-sm border border-transparent hover:border-zinc-200 transition-all"
        >
          <Plus className="size-3.5" />
          Add Family Member
        </button>
      </div>
    ),

    summary: (
      <div className="space-y-5">
        {!summary?.case_summary && !summary?.caseSummary ? (
          <div className="text-center py-10">
            <Sparkles className="size-8 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm font-black text-zinc-900">No AI Summary Yet</p>
            <p className="text-xs font-semibold text-zinc-500 mt-1">
              Click &quot;AI Summary&quot; in the sidebar to generate one.
            </p>
          </div>
        ) : (
          <>
            {/* Decision badge — minimal, strong */}
            {(summary?.recommendationDecision || summary?.recommendation_decision) && (
              <div className="flex items-center gap-3">
                <div className={cn(
                  'size-2.5 rounded-full shrink-0',
                  ((summary.recommendationDecision || summary.recommendation_decision) === 'Likely Accept' || (summary.recommendationDecision || summary.recommendation_decision) === 'Potential Accept') && 'bg-emerald-500',
                  (summary.recommendationDecision || summary.recommendation_decision) === 'Uncertain' && 'bg-amber-500',
                  ((summary.recommendationDecision || summary.recommendation_decision) === 'Potential Decline' || (summary.recommendationDecision || summary.recommendation_decision) === 'Likely Decline') && 'bg-red-500',
                )} />
                <span className="text-sm font-black text-zinc-900">
                  {summary.recommendationDecision || summary.recommendation_decision}
                </span>
              </div>
            )}

            {/* Case Summary */}
            {(summary?.caseSummary || summary?.case_summary) && (
              <div>
                <span className={labelClasses}>Case Summary</span>
                <p className="text-[13px] font-semibold text-zinc-900 mt-1.5 leading-relaxed whitespace-pre-wrap">
                  {summary.caseSummary || summary.case_summary}
                </p>
              </div>
            )}

            {/* Recommendation Reason */}
            {(summary?.recommendationReason || summary?.recommendation_reason) && (
              <div>
                <span className={labelClasses}>Recommendation</span>
                <p className="text-[13px] font-semibold text-zinc-900 mt-1.5 leading-relaxed whitespace-pre-wrap">
                  {summary.recommendationReason || summary.recommendation_reason}
                </p>
              </div>
            )}

            {/* Flags — clean, no garish backgrounds */}
            <div className="space-y-3">
              {(summary?.greenFlags || summary?.green_flags) && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="size-2 rounded-full bg-emerald-500 shrink-0" />
                    <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Strengths</span>
                  </div>
                  <p className="text-[13px] font-semibold text-zinc-900 leading-relaxed whitespace-pre-wrap pl-4">
                    {summary.greenFlags || summary.green_flags}
                  </p>
                </div>
              )}
              {(summary?.yellowFlags || summary?.yellow_flags) && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="size-2 rounded-full bg-amber-500 shrink-0" />
                    <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Watch Items</span>
                  </div>
                  <p className="text-[13px] font-semibold text-zinc-900 leading-relaxed whitespace-pre-wrap pl-4">
                    {summary.yellowFlags || summary.yellow_flags}
                  </p>
                </div>
              )}
              {(summary?.redFlags || summary?.red_flags) && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="size-2 rounded-full bg-red-500 shrink-0" />
                    <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Concerns</span>
                  </div>
                  <p className="text-[13px] font-semibold text-zinc-900 leading-relaxed whitespace-pre-wrap pl-4">
                    {summary.redFlags || summary.red_flags}
                  </p>
                </div>
              )}
            </div>

            {/* Counterpoint */}
            {(summary?.recommendationCounterpoint || summary?.recommendation_counterpoint) && (
              <div className="border-t border-zinc-100 pt-4">
                <span className={labelClasses}>
                  {summary.recommendationCounterpointHeading || summary.recommendation_counterpoint_heading || 'Counterpoint'}
                </span>
                <p className="text-[13px] font-semibold text-zinc-900 mt-1.5 leading-relaxed whitespace-pre-wrap">
                  {summary.recommendationCounterpoint || summary.recommendation_counterpoint}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    ),
  };

  return (
    <div className="h-full overflow-y-auto bg-zinc-50">
      <div className="w-full max-w-4xl mx-auto p-4 space-y-3">
        {/* Case Description */}
        {onCaseDescriptionChange && (
          <div className="bg-white rounded-2xl border border-zinc-200 p-4">
            <div className="flex items-center gap-2 mb-2.5">
              <Sparkles className="size-3.5 text-zinc-400" />
              <span className="text-xs font-black text-zinc-900 uppercase tracking-wider">Case Description</span>
            </div>
            <textarea
              value={caseDescription ?? ''}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              rows={3}
              className={cn(inputClasses, 'resize-none min-h-[70px]')}
              placeholder="Describe the case details, circumstances, and any important context..."
            />
          </div>
        )}

        {/* Horizontal tab bar */}
        <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
          {/* Tab pills */}
          <div className="flex items-center gap-0.5 p-1.5 border-b border-zinc-100 bg-zinc-50/50">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all duration-150',
                  activeTab === tab.key
                    ? 'bg-zinc-900 text-white shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100',
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Active tab content */}
          <div className="p-4">
            {tabContent[activeTab]}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewScreen;
